import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { StripeError } from '@stripe/stripe-js';
import type { TFunction } from 'i18next';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../ui';
import { Group } from '@folio/components/ui/flex';

/** Stripe codes whose own message is too vague to act on, so we ship copy for them. */
const CODES_WITH_OWN_COPY = ['setup_intent_unexpected_state', 'api_connection_error'];

const setupErrorText = (error: StripeError, t: TFunction): string => {
  const code = error.code ?? error.type;
  const text = CODES_WITH_OWN_COPY.includes(code)
    ? t(`customerPaymentMethods.errors.${code}`)
    : (error.message ?? t('customerPaymentMethods.errors.generic'));

  // Keep the Stripe code visible so support can triage from a screenshot alone.
  return error.code ? `${text} (${error.code})` : text;
};

export const CheckoutForm = ({ confirmSuccess }: { confirmSuccess: () => Promise<void> }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string>();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    if (!stripe || !elements || !ready) {
      // Stripe.js hasn't loaded or the Payment Element hasn't mounted and
      // emitted `ready` yet — confirming now throws an IntegrationError.
      return;
    }

    setLoading(true);
    setError(undefined);
    const result = await stripe.confirmSetup({
      //`Elements` instance that was used to create the Payment Element
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (result.error) {
      // The Payment Element renders `validation_error` inline on the field it owns;
      // declines, unusable intents and network failures reach us only here.
      if (result.error.type !== 'validation_error') {
        setError(setupErrorText(result.error, t));
      }
    } else {
      setSuccess(true);

      // Your customer will be redirected to your `return_url`. For some payment
      // methods like iDEAL, your customer will be redirected to an intermediate
      // site first to authorize the payment, then redirected to the `return_url`.
      await confirmSuccess();
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="checkout-form">
      <PaymentElement options={{ readOnly: success }} onReady={() => setReady(true)} />
      {error && (
        <div
          role="alert"
          className="mt-4 text-sm text-destructive"
          data-testid="checkout-form-error"
        >
          {error}
        </div>
      )}
      <Group align="stretch" justify="end" gap={0} className="mt-4">
        {stripe && ready && !success && (
          <Button type="submit" loading={loading}>
            {t('customerPaymentMethods.actions.submit')}
          </Button>
        )}
      </Group>
    </form>
  );
};
